import { useTranslation } from "react-i18next";
import { Stack } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import type { LineItemMessages as LineItemMessageType } from "~/domains/cart/hooks/useLineItemData";

export interface LineItemMessagesProps {
  /** Array of messages to display */
  messages: LineItemMessageType[];
  /** Whether product is PMA type - shows PMA info message */
  isPMA?: boolean;
  /** Whether product is Optime type - shows Optime message */
  isOptime?: boolean;
  /** Whether quantity is visible - if false, shows subscription billing info */
  quantityVisible?: boolean;
  /** Optional className for the container */
  className?: string;
}

/**
 * Renders line item messages including:
 * - Dynamic messages from cart/validation
 * - PMA product info message
 * - Subscription billing info
 * - Optime solutions message
 */
export const LineItemMessagesSection = ({
  messages,
  isPMA = false,
  isOptime = false,
  quantityVisible = true,
  className,
}: LineItemMessagesProps) => {
  const { t } = useTranslation();

  const hasContent = messages.length > 0 || isPMA || !quantityVisible || isOptime;

  if (!hasContent) {
    return null;
  }

  return (
    <Stack gap={1} className={className}>
      {messages.map((message) => (
        <Alert key={message.key} severity={message.type === "fatal" ? "error" : message.type}>
          {t(message.key, message.params)}
        </Alert>
      ))}
      {isPMA && <InfoProductMessagePMA />}
      {!quantityVisible && <Alert severity="info">{t("subscriptionProduct.DST.billingInfo")}</Alert>}
      {isOptime && <OptimeSolutionsMessage />}
    </Stack>
  );
};
